import React from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { ShieldAlert } from "lucide-react";
import { selectAuth } from "../redux/slices/authSlice";
import { usePermission } from "../hooks/usePermission";
import { FLEETRIFYROUTEPATH } from "./routesPath";
import DashboardLayout from "../components/DashboardLayout";

interface PermissionGateProps {
  permissions?: string[];
  children: React.ReactNode;
}

// Rendered in place of the gated page, so it must stay wrapped in
// DashboardLayout itself — routes provide their own SideNav/TopNav shell via
// DashboardLayout rather than inheriting one from ProtectedRoute (which is
// just an <Outlet/>), so a bare replacement here would strand the user on a
// screen with no navigation at all.
const NoAccess: React.FC = () => {
  const navigate = useNavigate();
  return (
    <DashboardLayout title="Access Denied">
      <div className="min-h-[60vh] flex flex-col items-center justify-center text-center p-6 gap-3">
        <ShieldAlert size={40} className="text-gray-400" />
        <p className="text-gray-700 font-medium">You don't have permission to view this page.</p>
        <button
          onClick={() => navigate(FLEETRIFYROUTEPATH.DASHBOARD)}
          className="mt-2 px-4 py-2 rounded-lg text-sm font-medium text-white"
          style={{ background: "#143887" }}
        >
          Go to Dashboard
        </button>
      </div>
    </DashboardLayout>
  );
};

// Blocks direct navigation (URL bar, bookmarks, links like the TopNav avatar)
// to a route the current user lacks permission for — mirrors the exact
// access logic SideNav uses to decide whether to show the nav item at all,
// so a hidden module can't still be reached by typing/clicking its URL.
//
// Denial renders an inline message rather than auto-redirecting: an automatic
// redirect back to Dashboard would infinite-loop for any role missing
// "view dashboard", since Dashboard is itself a gate-able route.
const PermissionGate: React.FC<PermissionGateProps> = ({ permissions, children }) => {
  const auth = useSelector(selectAuth);
  const { permissionNames, isLoading } = usePermission();

  const noPermissionsRequired = !permissions || permissions.length === 0;

  const isSuperAdmin =
    !!auth?.user?.is_head_of_org ||
    auth?.isAdmin ||
    auth?.user?.role?.name === "Super Admin";

  // The permission list loads async (no PersistGate delays first render), so
  // on a hard refresh `permissionNames` is briefly empty before the real data
  // arrives. Don't redirect on that empty snapshot — wait for the check to
  // actually resolve, otherwise an authorized user gets bounced for a beat.
  if (!noPermissionsRequired && !isSuperAdmin && isLoading) {
    return null;
  }

  const hasAccess =
    noPermissionsRequired ||
    isSuperAdmin ||
    permissions!.some((p) => permissionNames.includes(p));

  if (!hasAccess) {
    return <NoAccess />;
  }

  return <>{children}</>;
};

export default PermissionGate;
